import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  BankAccountDto,
  BankIngestCommitFields,
  BankIngestCommitResult,
  BankIngestPreviewFields,
  BankIngestPreviewResult,
  CreateBankAccountRequest,
  ManualBankEntryRequest,
  ManualBankEntryResult,
} from '@signalvote/shared';
import { bankIngestCommitRequest, bankIngestPreviewRequest } from '@signalvote/shared';
import { financeQueries, queryKeys } from '@/lib/queryKeys';
import { apiJson, apiResponse } from './apiFetch';

/**
 * PPR-004: bank-ingestion hooks.
 *
 *   * <code>useBankAccounts</code> — populates the pre-upload account picker
 *     and the manual-entry account dropdown.
 *   * <code>usePreviewBankMapping</code> — drives the 5-row preview in the
 *     column-mapping confirmation UI.
 *   * <code>useCommitBankIngest</code> — finalize an ImportBatch. Invalidates
 *     the inbox + reconciliation caches on success.
 *   * <code>useIngestManualBankEntry</code> — manual escape hatch.
 */
export function useBankAccounts() {
  return useQuery({
    queryKey: queryKeys.finance.bankIngestion.accounts(),
    queryFn: () =>
      apiJson<BankAccountDto[]>('/api/finance/bank-ingestion/accounts', {
        error: (err) => new Error(err.error || 'Failed to load bank accounts'),
      }),
  });
}

/**
 * Create a bank account inline from the import flow ("pick or create"), so a
 * treasurer with a brand-new statement isn't bounced to the Setup tab. The
 * display name is the only field — the column-mapping profile is learned on the
 * first mapped import (the existing `createdCustom` flow), not here. Invalidates
 * the accounts cache so the picker refetches and shows the new account.
 */
export function useCreateBankAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: CreateBankAccountRequest) => {
      const response = await apiResponse('/api/finance/bank-ingestion/accounts', {
        method: 'POST',
        json: request,
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new BankIngestErrorWithCode(
          body.error || 'Failed to create bank account',
          body.code,
        );
      }
      return body as BankAccountDto;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.bankIngestion.accounts() });
    },
  });
}

export function usePreviewBankMapping() {
  return useMutation({
    mutationFn: async (request: BankIngestPreviewFields) => {
      const response = await apiResponse('/api/finance/bank-ingestion/preview', {
        method: 'POST',
        json: bankIngestPreviewRequest(request),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new BankIngestErrorWithCode(
          body.error || 'Failed to preview mapping',
          body.code,
        );
      }
      return body as BankIngestPreviewResult;
    },
  });
}

export function useCommitBankIngest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: BankIngestCommitFields) => {
      const response = await apiResponse('/api/finance/bank-ingestion/commit', {
        method: 'POST',
        json: bankIngestCommitRequest(request),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new BankIngestErrorWithCode(
          body.error || 'Failed to commit ingest',
          body.code,
        );
      }
      return body as BankIngestCommitResult;
    },
    onSuccess: () => {
      // financeQueries already matches every finance query (bank-ingestion, detector,
      // ledger, …), so a curated bankIngestion/detector invalidation alongside it is a
      // provable no-op.
      queryClient.invalidateQueries(financeQueries);
    },
  });
}

export function useIngestManualBankEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: ManualBankEntryRequest) => {
      const response = await apiResponse('/api/finance/bank-ingestion/manual', {
        method: 'POST',
        json: request,
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new BankIngestErrorWithCode(
          body.error || 'Failed to add manual entry',
          body.code,
        );
      }
      return body as ManualBankEntryResult;
    },
    onSuccess: () => {
      // financeQueries already matches bank-ingestion, so a curated
      // bankIngestion invalidation alongside it is a provable no-op.
      queryClient.invalidateQueries(financeQueries);
    },
  });
}

/**
 * Backend error code (from `BankIngestionErrorCodes`)
 * attached to thrown errors so callers can switch on `code` instead of
 * parsing the message. Mirrors the PPR-003 `DetectorErrorWithCode` pattern.
 */
export class BankIngestErrorWithCode extends Error {
  public readonly code?: string;
  constructor(message: string, code?: string) {
    super(message);
    this.code = code;
  }
}
